/**
 * useAgentChat — role-scoped chat hook backed by `useChatStore`.
 *
 * The WS top-level dispatcher in `api/ws.ts` already feeds streams, tool calls,
 * and turn-end signals into the store; this hook just exposes a stable view
 * of the thread for a given (companyId, role) plus a `send` action.
 *
 * Sending uses REST (`POST /v1/agents/messages`) rather than `agent.message`
 * over WS. This matches the hub's preference: REST gives us a synchronous
 * `message_id` for echoing the optimistic user bubble, and the agent's
 * streaming reply still flies back over the existing WS.
 */

import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AgentRole } from "@paperclip-cloud/shared";
import { agentMessagesApi, type AgentMessage } from "../api/queries/agents";
import {
  useChatStore,
  useChatThread,
  type ChatMessage,
} from "../stores/chat";

export type AgentChatStatus = "idle" | "streaming" | "error";

function toChatMessages(messages: AgentMessage[]): ChatMessage[] {
  return messages.map((m) => ({
    id: m.id,
    author: m.author,
    content: m.content,
    streaming: false,
    toolCalls: [],
    at: m.created_at,
    compressed_into_id: m.compressed_into_id ?? null,
  }));
}

export function useAgentChat(companyId: string | null, role: AgentRole) {
  const thread = useChatThread(role);
  const ensureCompany = useChatStore((s) => s.ensureCompany);
  const loadHistory = useChatStore((s) => s.loadHistory);
  const appendUser = useChatStore((s) => s.appendUser);
  const setDraft = useChatStore((s) => s.setDraft);

  // Reset role threads when the company changes.
  useEffect(() => {
    if (!companyId) return;
    ensureCompany(companyId);
  }, [companyId, ensureCompany]);

  // Hydrate persisted history once per (companyId, role).
  const history = useQuery({
    queryKey: ["agent-messages", companyId, role],
    queryFn: () => agentMessagesApi.list(companyId!, role),
    enabled: !!companyId,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!history.data) return;
    loadHistory(role, toChatMessages(history.data.messages));
  }, [history.data, role, loadHistory]);

  const send = useMemo(
    () => async (content: string, workingDir?: string | null) => {
      if (!companyId || !content.trim()) return;
      appendUser(role, content);
      try {
        await agentMessagesApi.send(companyId, role, content, workingDir);
      } catch (err) {
        // Failure surfaces via the error state on the next render; we don't
        // pop a toast here — the panel can decide.
        console.warn("[chat] send failed", err);
        throw err;
      }
    },
    [companyId, role, appendUser],
  );

  const status: AgentChatStatus = thread.streaming
    ? "streaming"
    : history.isError
      ? "error"
      : "idle";

  return {
    messages: thread.messages,
    draft: thread.draft,
    setDraft: (s: string) => setDraft(role, s),
    send,
    status,
  };
}
